/**
 * @file Общий опрос серверной задачи: сборки, архива, удаления, переименования.
 *
 * Только done подтверждает успех. unknown означает потерю подтверждения,
 * а не отказ сервера: вызывающий код сам решает, что делать с временными файлами.
 */
import { abortableDelay, withAbort } from './abort.ts'

// Опрос статуса — служебный запрос и квотой трафика не ограничивается.
type TaskResult = 'done' | 'failed' | 'unknown' | 'access'

export async function waitForTask(
    read: (signal: AbortSignal) => Promise<Response>,
    signal: AbortSignal,
    wait = abortableDelay,
    maxPolls = 300,
): Promise<TaskResult> {
    let failures = 0

    for (let poll = 0; poll < maxPolls; poll++) {
        signal.throwIfAborted()
        let response: Response

        try {
            response = await withAbort(read(signal), signal)
            signal.throwIfAborted()
            if ([401, 403].includes(response.status)) return 'access'
            if (!response.ok) throw new Error('Task unavailable')

            // Считаем только ошибки подряд: успешный опрос начинает отсчёт заново.
            failures = 0
        } catch {
            signal.throwIfAborted()
            if (++failures >= 3) return 'unknown'

            await wait(1000, signal)
            continue
        }

        // Отмена должна прерывать и запрос, и чтение его тела.
        const data: unknown = await withAbort(
            response.json().catch(() => null),
            signal,
        )
        signal.throwIfAborted()
        if (!data || typeof data !== 'object' || !('status' in data))
            return 'unknown'

        if (data.status === 'done') return 'done'
        if (data.status === 'error') return 'failed'
        if (data.status !== 'in-progress') return 'unknown'

        await wait(1000, signal)
    }

    return 'unknown'
}
